import React, { useState, useEffect } from 'react';
import './HRLateWorkers.css';

const BASE = 'https://fotileams.com/api';

function authHeaders() {
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
  };
}

function getInitials(name) {
  if (!name) return '??';
  const p = name.trim().split(' ');
  return (p[0][0] + (p[p.length - 1][0] || '')).toUpperCase();
}

function getAvatarColor(name) {
  const colors = ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316'];
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h += name.charCodeAt(i);
  return colors[h % colors.length];
}

// Minutes -> "2h 15m"
function fmtMins(m) {
  const n = parseInt(m) || 0;
  if (n === 0) return '0m';
  const h = Math.floor(n / 60);
  const mm = n % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}m`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-PK', { weekday: 'short', day: 'numeric', month: 'short' });
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function HRLateWorkers() {
  const [tab, setTab]               = useState('manage'); // manage | balances | ledger
  const [employees, setEmployees]   = useState([]);
  const [search, setSearch]         = useState('');
  const [loadingEmp, setLoadingEmp] = useState(true);
  const [toggling, setToggling]     = useState(null);

  const [balances, setBalances]     = useState([]);
  const [loadingBal, setLoadingBal] = useState(true);

  const [ledger, setLedger]         = useState([]);
  const [loadingLed, setLoadingLed] = useState(true);
  const [month, setMonth]           = useState(new Date().getMonth() + 1);
  const [year, setYear]             = useState(new Date().getFullYear());

  useEffect(() => { fetchEmployees(); }, [search]);
  useEffect(() => { if (tab === 'balances') fetchBalances(); }, [tab]);
  useEffect(() => { if (tab === 'ledger') fetchLedger(); }, [tab, month, year]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmp(true);
      const url = search
        ? `${BASE}/lateworkers/employees?search=${encodeURIComponent(search)}`
        : `${BASE}/lateworkers/employees`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      setEmployees(data.employees || []);
    } catch (err) { console.error(err); }
    finally { setLoadingEmp(false); }
  };

  const fetchBalances = async () => {
    try {
      setLoadingBal(true);
      const res = await fetch(`${BASE}/lateworkers/balances`, { headers: authHeaders() });
      const data = await res.json();
      setBalances(data.late_workers || []);
    } catch (err) { console.error(err); }
    finally { setLoadingBal(false); }
  };

  const fetchLedger = async () => {
    try {
      setLoadingLed(true);
      const res = await fetch(`${BASE}/lateworkers/ledger?month=${month}&year=${year}`, { headers: authHeaders() });
      const data = await res.json();
      setLedger(data.ledger || []);
    } catch (err) { console.error(err); }
    finally { setLoadingLed(false); }
  };

  const toggleLateWorker = async (emp) => {
    setToggling(emp.id);
    try {
      await fetch(`${BASE}/lateworkers/${emp.id}/toggle`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ is_late_worker: !emp.is_late_worker }),
      });
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, is_late_worker: !e.is_late_worker } : e));
    } catch (err) { console.error(err); }
    finally { setToggling(null); }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const lateWorkerCount = employees.filter(e => e.is_late_worker).length;

  return (
    <div className="lw-root">
      <div className="lw-header">
        <div>
          <h1 className="lw-title">🌙 Late Workers</h1>
          <p className="lw-sub">
            Staff who stay late and arrive late the next morning. Extra minutes are banked as credit
            and automatically forgive a later arrival — never auto-clocked out.
          </p>
        </div>
      </div>

      <div className="lw-note">
        <span className="lw-note-icon">🔒</span>
        <div>
          <b>Employees cannot see their balance.</b> They only receive a message each time credit is
          applied, framed as a company courtesy with a reminder to be on time — so it never becomes
          an expected allowance. Only HR sees the numbers below.
        </div>
      </div>

      <div className="lw-tabs">
        <button className={`lw-tab ${tab === 'manage' ? 'lw-tab-active' : ''}`} onClick={() => setTab('manage')}>
          Manage ({lateWorkerCount})
        </button>
        <button className={`lw-tab ${tab === 'balances' ? 'lw-tab-active' : ''}`} onClick={() => setTab('balances')}>
          Balances
        </button>
        <button className={`lw-tab ${tab === 'ledger' ? 'lw-tab-active' : ''}`} onClick={() => setTab('ledger')}>
          Credit Ledger
        </button>
      </div>

      {/* ── MANAGE ── */}
      {tab === 'manage' && (
        <>
          <div className="lw-search-wrap">
            <span className="lw-search-icon">🔍</span>
            <input
              className="lw-search"
              placeholder="Search name or ID…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>

          {loadingEmp ? (
            <div className="lw-loading"><div className="lw-spinner" /></div>
          ) : (
            <div className="lw-emp-list">
              {employees.map(emp => (
                <div key={emp.id} className={`lw-emp-row ${emp.is_late_worker ? 'lw-emp-row-active' : ''}`}>
                  <div className="lw-emp-av" style={{ background: getAvatarColor(emp.full_name) }}>
                    {getInitials(emp.full_name)}
                  </div>
                  <div className="lw-emp-info">
                    <div className="lw-emp-name">{emp.full_name}</div>
                    <div className="lw-emp-meta">
                      {emp.employee_id} · {emp.department || 'Unassigned'} · {emp.location_name || '—'}
                    </div>
                  </div>
                  <button
                    className={`lw-toggle ${emp.is_late_worker ? 'lw-toggle-on' : ''}`}
                    onClick={() => toggleLateWorker(emp)}
                    disabled={toggling === emp.id}
                  >
                    <span className="lw-toggle-knob" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── BALANCES ── */}
      {tab === 'balances' && (
        loadingBal ? (
          <div className="lw-loading"><div className="lw-spinner" /></div>
        ) : balances.length === 0 ? (
          <div className="lw-empty">
            <div className="lw-empty-icon">🌙</div>
            <div className="lw-empty-title">No late workers yet</div>
            <div className="lw-empty-sub">Turn people on in the Manage tab first.</div>
          </div>
        ) : (
          <div className="lw-bal-list">
            {balances.map(b => (
              <div key={b.id} className="lw-bal-card">
                <div className="lw-bal-av" style={{ background: getAvatarColor(b.full_name) }}>
                  {getInitials(b.full_name)}
                </div>
                <div className="lw-bal-info">
                  <div className="lw-bal-name">{b.full_name}</div>
                  <div className="lw-bal-meta">{b.employee_id} · {b.department || '—'} · {b.city || '—'}</div>
                </div>
                <div className="lw-bal-nums">
                  <div className="lw-bal-stat">
                    <span className="lw-bal-lbl">Earned</span>
                    <span className="lw-bal-val lw-earned">{fmtMins(b.earned_minutes)}</span>
                  </div>
                  <div className="lw-bal-stat">
                    <span className="lw-bal-lbl">Used</span>
                    <span className="lw-bal-val lw-spent">{fmtMins(b.spent_minutes)}</span>
                  </div>
                  <div className="lw-bal-stat">
                    <span className="lw-bal-lbl">Balance</span>
                    <span className="lw-bal-val lw-balance">{fmtMins(b.balance_minutes)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {/* ── LEDGER ── */}
      {tab === 'ledger' && (
        <>
          <div className="lw-period-row">
            <select className="lw-period-select" value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="lw-period-select" value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {loadingLed ? (
            <div className="lw-loading"><div className="lw-spinner" /></div>
          ) : ledger.length === 0 ? (
            <div className="lw-empty">
              <div className="lw-empty-icon">📒</div>
              <div className="lw-empty-title">No credit activity</div>
              <div className="lw-empty-sub">
                Nothing earned or used in {MONTHS[month - 1]} {year}.
              </div>
            </div>
          ) : (
            <div className="lw-led-list">
              {ledger.map(l => (
                <div key={l.id} className={`lw-led-row lw-led-${l.kind}`}>
                  <div className={`lw-led-badge lw-badge-${l.kind}`}>
                    {l.kind === 'earned' ? '+' : '−'}{fmtMins(l.minutes)}
                  </div>
                  <div className="lw-led-info">
                    <div className="lw-led-name">{l.full_name} <span className="lw-led-code">{l.emp_code}</span></div>
                    <div className="lw-led-note">{l.note || '—'}</div>
                  </div>
                  <div className="lw-led-date">{fmtDate(l.date)}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}